import { useState } from 'react';
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import {
  DetectedWallet,
  useWalletSign,
} from '../../xpx-perps-withdrawal/useWalletSign';
import {
  WithdrawalItem,
  retryFailedWithdrawal,
} from '../../../services/xbitRelayWithdrawalsService';

type Props = {
  record: WithdrawalItem;
  onClose(): void;
};

const TRON_CHAIN_ID = 728126428;

const isUserRejection = (err: any): boolean => {
  const code = err?.code ?? err?.cause?.code;
  if (code === 4001 || code === 'ACTION_REJECTED') return true;
  const msg = (err?.message || '').toLowerCase();
  return msg.includes('reject') || msg.includes('denied');
};

const buildMessage = (row: WithdrawalItem, timestamp: number) => {
  const chainIdInt = parseInt(String(row.chainId), 10);
  return (
    `RetryFailedWithdrawal\n` +
    `WithdrawalID: ${row.id}\n` +
    `Provider: ${row.provider}\n` +
    `ChainID: ${chainIdInt}\n` +
    `TokenAddress: ${row.token}\n` +
    `ToAddress: ${row.toAddress}\n` +
    `Amount: ${row.amount}\n` +
    `PlatformFee: ${row.platformFee}\n` +
    `Timestamp: ${timestamp}`
  );
};

export function RetryFailedDialog({ record, onClose }: Props) {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { detectedWallets, connect, address, signMessage } = useWalletSign();
  const [selectedWallet, setSelectedWallet] = useState<DetectedWallet | null>(
    null,
  );
  const [connecting, setConnecting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [evmNonce, setEvmNonce] = useState('');
  const [gasLimit, setGasLimit] = useState('');
  const [gasPrice, setGasPrice] = useState('');
  const [feeLimit, setFeeLimit] = useState('');

  const isTron = Number(record.chainId) === TRON_CHAIN_ID;

  const handleConnect = async () => {
    if (!selectedWallet) return;
    setConnecting(true);
    try {
      await connect(selectedWallet.provider);
    } catch (err: any) {
      if (!isUserRejection(err)) {
        notify(err?.message || t('common.error'), { type: 'error' });
      }
    } finally {
      setConnecting(false);
    }
  };

  const handleSignAndRetry = async () => {
    if (!address) return;
    setLoading(true);
    try {
      const timestamp = Date.now();
      const message = buildMessage(record, timestamp);
      const signature = await signMessage(message);
      const body: any = {
        admin_id: address.toLowerCase(),
        signature,
        timestamp,
      };
      if (isTron) {
        if (feeLimit.trim()) body.fee_limit = feeLimit.trim();
      } else {
        if (evmNonce.trim()) body.evm_nonce = evmNonce.trim();
        if (gasLimit.trim()) body.gas_limit = gasLimit.trim();
        if (gasPrice.trim()) body.gas_price = gasPrice.trim();
      }
      const { data } = await retryFailedWithdrawal(record.id, body);
      if (!data.success) {
        notify(data.message || t('module.xbitRelayWithdrawals.retryRejected'), {
          type: 'error',
        });
        return;
      }
      notify(t('module.xbitRelayWithdrawals.retryQueued'), { type: 'success' });
      refresh();
      onClose();
    } catch (err: any) {
      if (isUserRejection(err)) return;
      notify(
        err?.response?.data?.message || err?.message || t('common.error'),
        { type: 'error' },
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogTitle>
        {t('module.xbitRelayWithdrawals.retryFailedTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          <Stack spacing={0.5}>
            <Typography variant="body2">
              <b>{t('module.xbitRelayWithdrawals.userId')}:</b> {record.userId}
            </Typography>
            <Typography variant="body2">
              <b>{t('module.xbitRelayWithdrawals.provider')}:</b>{' '}
              {record.provider}
            </Typography>
            <Typography variant="body2">
              <b>{t('module.xbitRelayWithdrawals.chainId')}:</b>{' '}
              {record.chainId}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
            >
              <b>{t('module.xbitRelayWithdrawals.toAddress')}:</b>{' '}
              {record.toAddress}
            </Typography>
            <Typography variant="body2">
              <b>{t('module.xbitRelayWithdrawals.amount')}:</b> {record.amount}{' '}
              {record.symbol || record.tokenSymbol || record.token}
            </Typography>
            <Typography variant="body2">
              <b>{t('module.xbitRelayWithdrawals.platformFee')}:</b> {record?.platformFee}{' '}
              {record.symbol || record.tokenSymbol || record.token}
            </Typography>
            {(record.errorMessage || record.errorMsg) && (
              <Typography variant="caption" color="error" sx={{ whiteSpace: 'pre-line' }}>
                <b>{t('module.xbitRelayWithdrawals.previousError')}:</b>{' '}
                {record.errorMessage || record.errorMsg}
              </Typography>
            )}
          </Stack>

          <Typography variant="caption" color="text.secondary">
            {t('module.xbitRelayWithdrawals.overridesOptional')}
          </Typography>
          {isTron ? (
            <TextField
              size="small"
              label="fee_limit (sun)"
              value={feeLimit}
              onChange={(e) => setFeeLimit(e.target.value)}
              disabled={loading}
            />
          ) : (
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                label="evm_nonce"
                value={evmNonce}
                onChange={(e) => setEvmNonce(e.target.value)}
                disabled={loading}
              />
              <TextField
                size="small"
                label="gas_limit"
                value={gasLimit}
                onChange={(e) => setGasLimit(e.target.value)}
                disabled={loading}
              />
              <TextField
                size="small"
                label="gas_price (wei)"
                value={gasPrice}
                onChange={(e) => setGasPrice(e.target.value)}
                disabled={loading}
              />
            </Stack>
          )}

          {detectedWallets.length === 0 ? (
            <Typography variant="body2" color="error">
              {t('module.xbitRelayWithdrawals.noWalletFound')}
            </Typography>
          ) : (
            <FormControl size="small" fullWidth>
              <InputLabel>
                {t('module.xbitRelayWithdrawals.selectWallet')}
              </InputLabel>
              <Select
                label={t('module.xbitRelayWithdrawals.selectWallet')}
                value={selectedWallet?.name ?? ''}
                onChange={(e) => {
                  const w = detectedWallets.find(
                    (x) => x.name === e.target.value,
                  );
                  setSelectedWallet(w ?? null);
                }}
                disabled={Boolean(address) || connecting || loading}
              >
                {detectedWallets.map((w) => (
                  <MenuItem key={w.name} value={w.name}>
                    {w.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {!address && selectedWallet && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleConnect}
              disabled={connecting}
              startIcon={connecting ? <CircularProgress size={14} /> : null}
            >
              {t('module.xbitRelayWithdrawals.connect')}
            </Button>
          )}

          {address && (
            <Typography variant="caption" color="text.secondary">
              {t('module.xbitRelayWithdrawals.connectedAs')}: {address}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onClose}
          disabled={loading || connecting}
        >
          {t('actions.cancel')}
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleSignAndRetry}
          disabled={!address || loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {t('module.xbitRelayWithdrawals.signAndRetry')}
        </Button>
      </DialogActions>
    </>
  );
}
